// ── Media compression and processing utilities ────────────────────────────
// src/lib/mediaUtils.ts

export const MAX_PHOTO_MB  = 10
export const MAX_VIDEO_MB  = 48   // just under 50MB Supabase limit
export const MAX_VOICE_MB  = 10
export const MAX_PHOTO_B   = MAX_PHOTO_MB * 1024 * 1024
export const MAX_VIDEO_B   = MAX_VIDEO_MB * 1024 * 1024
export const MAX_VOICE_B   = MAX_VOICE_MB * 1024 * 1024

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function getSupportedMimeType(): string {
  const types = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ]
  for (const t of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t
  }
  return ''
}

export function getAudioExtension(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

// ── Compress an image file using canvas ──────────────────────────────────
export async function compressImage(file: File, maxMB = MAX_PHOTO_MB): Promise<File> {
  return new Promise((resolve, reject) => {
    const maxBytes = maxMB * 1024 * 1024

    // Already small enough
    if (file.size <= maxBytes) { resolve(file); return }

    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const canvas = document.createElement('canvas')

      // Scale down if very large
      let { width, height } = img
      const MAX_DIM = 2048
      if (width > MAX_DIM || height > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / width, MAX_DIM / height)
        width  = Math.round(width  * ratio)
        height = Math.round(height * ratio)
      }
      canvas.width  = width
      canvas.height = height

      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height)

      // Try quality 0.8 first, then 0.6 if still too big
      const tryExport = (quality: number) => {
        canvas.toBlob(blob => {
          if (!blob) { reject(new Error('Failed to compress image')); return }
          if (blob.size <= maxBytes || quality <= 0.4) {
            const compressed = new File([blob], file.name, { type: 'image/jpeg' })
            resolve(compressed)
          } else {
            tryExport(quality - 0.2)
          }
        }, 'image/jpeg', quality)
      }
      tryExport(0.8)
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = url
  })
}

// ── Compress video using canvas frame-by-frame re-encoding ───────────────
// Note: True video compression requires ffmpeg.wasm (heavy).
// This lighter approach uses MediaRecorder to re-encode at lower bitrate.
export async function compressVideo(
  file: File,
  onProgress?: (pct: number) => void
): Promise<File> {
  const maxBytes = MAX_VIDEO_B

  // Already small enough
  if (file.size <= maxBytes) { return file }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const url   = URL.createObjectURL(file)
    video.src   = url
    video.muted = true
    video.playsInline = true

    video.onloadedmetadata = () => {
      const duration = video.duration
      const canvas   = document.createElement('canvas')

      // Scale down resolution for compression
      const MAX_WIDTH = 1280
      let w = video.videoWidth
      let h = video.videoHeight
      if (w > MAX_WIDTH) {
        h = Math.round(h * (MAX_WIDTH / w))
        w = MAX_WIDTH
      }
      canvas.width  = w
      canvas.height = h

      const ctx    = canvas.getContext('2d')!
      const stream = canvas.captureStream(24) // 24fps

      // Pick supported video format
      const videoMime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm'

      // Target bitrate: aim for ~40MB total
      const targetBits    = 40 * 1024 * 1024 * 8
      const videoBitrate  = Math.min(
        Math.floor(targetBits / Math.max(duration, 1)),
        2_500_000  // cap at 2.5Mbps
      )

      const recorder = new MediaRecorder(stream, {
        mimeType:    videoMime,
        videoBitsPerSecond: videoBitrate,
      })

      const chunks: BlobPart[] = []
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
      recorder.onstop = () => {
        URL.revokeObjectURL(url)
        const blob = new Blob(chunks, { type: videoMime })
        const ext  = 'webm'
        const name = file.name.replace(/\.[^.]+$/, '') + `-compressed.${ext}`
        resolve(new File([blob], name, { type: videoMime }))
      }

      recorder.start(100)
      video.play()

      let lastTime = -1
      const draw = () => {
        if (video.ended || video.paused) {
          recorder.stop()
          return
        }
        if (video.currentTime !== lastTime) {
          ctx.drawImage(video, 0, 0, w, h)
          lastTime = video.currentTime
          if (onProgress && duration > 0) {
            onProgress(Math.round((video.currentTime / duration) * 100))
          }
        }
        requestAnimationFrame(draw)
      }
      requestAnimationFrame(draw)

      video.onended = () => recorder.stop()
      video.onerror = () => reject(new Error('Video processing failed'))
    }

    video.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not load video for compression'))
    }
  })
}
